/**
 * Chat deck attachments (AB#452, plans/PPTX_IMPORT.md I7) — dropping (or picking, or pasting) a
 * PowerPoint deck into the chat composer, so the agent can import it with `import_pptx`.
 *
 * Unlike an image (lib/chat-images.ts), a deck does not ride inline with the message: the composer
 * stages it on the backend at once (`POST /v1/slides/imports/staged` — vetted, kept an hour for the
 * member who staged it), and the message carries only `{ stagedId, name }`. The agent hands that id
 * to the backend's import, which lays the slides out with its own converter — the deck's bytes never
 * go through the agent.
 */
import { api, type ChatDeck } from './api'
import { fileToBase64 } from './files'
import { PPTX_ACCEPT } from './slide-import'

/** Per message — kept in step with the passerelle's MAX_DECKS (agents/decks.ts). */
export const MAX_CHAT_DECKS = 3

/** What the composer's file picker offers on top of images. */
export const DECK_ACCEPT = PPTX_ACCEPT

const PPTX_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation'

/** A PowerPoint presentation, by its type or — a drag from some places gives none — its extension. */
export function isDeckFile(file: File): boolean {
  return file.type === PPTX_TYPE || /\.pptx$/i.test(file.name)
}

/** The deck files out of a paste's `clipboardData.files`, a drop's `dataTransfer.files` or a pick. */
export function deckFilesFrom(list: FileList | File[] | null | undefined): File[] {
  return Array.from(list ?? []).filter(isDeckFile)
}

/**
 * Stage a deck for the agent: read it, send it to the backend, keep what the message carries. A
 * refusal (encrypted, macros, too large…) rejects with the backend's error, for the composer to say.
 */
export async function stageChatDeck(file: File): Promise<ChatDeck> {
  const staged = await api.stageSlideImport({ base64: await fileToBase64(file), name: file.name || 'deck.pptx' })
  return { stagedId: staged.stagedId, name: staged.name }
}
